// Agent id → display-name resolution for the end-user layer (fn-48).
//
// Extracted from `conversation-list.tsx` (fn-48.3) so `<Chat>` shares
// the ONE resolution path instead of re-encoding it. `listAgents` is
// OPTIONAL on the adapter: absent → empty map (every caller renders
// the "Assistant" fallback); a fetch failure degrades the same way —
// name resolution is best-effort, never a render blocker.
import { useEffect, useState } from 'react';
import { usePlatformAdapter } from '../context.js';
const EMPTY_NAMES = new Map();
/**
 * Agent id → display name, fetched ONCE per adapter for the component
 * lifetime.
 */
export function useAgentNames() {
    const adapter = usePlatformAdapter();
    const [names, setNames] = useState(EMPTY_NAMES);
    useEffect(() => {
        // Reset FIRST: a previous adapter's names must never survive an
        // adapter swap (stale names across provider/account transitions
        // would leak the wrong agent identity — .flow memory:
        // context-derived-caches-must-reset-on). The new adapter either
        // repopulates the map or every row degrades to "Assistant".
        setNames(EMPTY_NAMES);
        const listAgents = adapter.listAgents?.bind(adapter);
        if (listAgents === undefined)
            return;
        let live = true;
        Promise.resolve()
            .then(() => listAgents())
            .then((page) => {
            if (!live)
                return;
            const map = new Map();
            for (const agent of page.data) {
                const name = agent.name.trim();
                if (name.length > 0)
                    map.set(agent.id, name);
            }
            setNames(map);
        })
            .catch(() => {
            // Degrade to "Assistant" everywhere; the surface itself still
            // renders.
            if (live)
                setNames(EMPTY_NAMES);
        });
        return () => {
            live = false;
        };
    }, [adapter]);
    return names;
}
//# sourceMappingURL=use-agent-names.js.map